import { StaffRole, DefaultRolePermissions } from "./types";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { createAuditEntry, AuditAction, AuditTargetType } from "./auditTrail";

export const initializeBusinessPermissions = async (
  businessId: string | undefined,
  userId: string
): Promise<void> => {
  try {
    if (!businessId) {
      throw new Error("Business ID is required");
    }

    if (
      !DefaultRolePermissions ||
      Object.keys(DefaultRolePermissions).length === 0
    ) {
      throw new Error("Default role permissions are not properly configured");
    }

    // Check if permissions already exist
    const permissionsRef = doc(db, "permissions", businessId);
    const permissionsDoc = await getDoc(permissionsRef);

    if (!permissionsDoc.exists()) {
      // Create default permissions structure
      const defaultPermissions = {
        businessId,
        userId,
        permissions: DefaultRolePermissions,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Save default permissions
      await setDoc(permissionsRef, defaultPermissions);

      // Create audit entry
      await createAuditEntry(
        businessId,
        userId,
        AuditAction.CREATE,
        AuditTargetType.PERMISSION,
        businessId,
        { permissions: DefaultRolePermissions },
        { type: "initial_setup" }
      );
    }
  } catch (error) {
    console.error("Error initializing business permissions:", error);
    throw error;
  }
};

export const ensureBusinessPermissions = async (
  businessId: string,
  userId: string
): Promise<void> => {
  try {
    if (!businessId) {
      throw new Error("Business ID is required");
    }

    if (
      !DefaultRolePermissions ||
      Object.keys(DefaultRolePermissions).length === 0
    ) {
      throw new Error("Default role permissions are not properly configured");
    }

    const permissionsRef = doc(db, "permissions", businessId);
    const permissionsDoc = await getDoc(permissionsRef);

    if (!permissionsDoc.exists()) {
      await initializeBusinessPermissions(businessId, userId);
    } else {
      const data = permissionsDoc.data();
      if (!data) {
        throw new Error("Failed to retrieve permissions data");
      }

      const currentPermissions = data.permissions || {};
      if (typeof currentPermissions !== "object") {
        throw new Error("Invalid permissions data structure");
      }

      // Check if all roles have permissions
      const allRolesHavePermissions = Object.values(StaffRole).every(
        (role) => role in currentPermissions
      );

      if (!allRolesHavePermissions) {
        // Update missing role permissions
        const updatedPermissions = {
          ...DefaultRolePermissions,
          ...currentPermissions,
        };

        await setDoc(
          permissionsRef,
          {
            permissions: updatedPermissions,
            updatedAt: new Date(),
          },
          { merge: true }
        );

        // Create audit entry
        await createAuditEntry(
          businessId,
          userId,
          AuditAction.UPDATE,
          AuditTargetType.PERMISSION,
          businessId,
          { permissions: updatedPermissions },
          { type: "missing_roles_update" }
        );
      }
    }
  } catch (error) {
    console.error("Error ensuring business permissions:", error);
    throw error;
  }
};
